import { useEffect, useMemo, useState } from "react";
import { DatepickerRange } from "../../../utils/daterange";
import { Box, Divider, Paper, Stack, Typography } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { sendServerRequest } from "../../../server/request";
import { openGlobalSnack } from "../../utils/snack";
import { CltPropostalInfo, CltPropostalStatus, CltSupportRowInfo, cltBankLabel } from "./types";
import { DisplayCard } from "../../whatsapp/datadisplay";
import { normalizeBrCurrency } from "../../../utils/brcurrency";
import { CltGraphTotalByGenericParam } from "./graph/geral";
import { chatSourceToPTBR } from "../../types/callsouce";
import MultipleSelectChip from "../../utils/forms/multipleselect";

export function CltDashboard(){
    const [date,setDate]=useState({
         start:new Date(),
         end:new Date(),
    });
    const [propostal,setPropostal]=useState<Array<CltPropostalInfo>>([]);
    const [rows,setRows]=useState<Array<CltSupportRowInfo>>([]);
    const [loadingConsult,setLoadingConsult]=useState(false);
    const [showedPropostsals,setShowedPropostals]=useState<Array<CltPropostalInfo>>([]);
    const [statusFilters,setStatusFilters]=useState<Array<string>>([]);
    const [usersIdFilters,setSelectedUserId]=useState<Array<string>>([]);
    const [bankFilters,setBankFilters]=useState<Array<string>>([]);
    const [userData,setUserData]=useState<Array<{id:number,email:string}>>([])

    // Opções de banco derivadas das propostas carregadas (v8 + Soma).
    const bankItems=useMemo(()=>{
       const set=new Set<string>();
       for(const p of propostal)if(p.bank)set.add(p.bank);
       return Array.from(set).sort().map(b=>({label:cltBankLabel(b),value:b}));
    },[propostal]);

    useEffect(()=>{
      if(!loadingConsult)return;
      sendServerRequest('POST','/server/clt/adm/dash',{
         fromDate:date.start,
         toDate:date.end,
      }).then((e)=>{
          setPropostal(e.data.data.propostals);
          setRows(e.data.data.supportRow);
          setUserData(e.data.data.users);
          setLoadingConsult(false);
          openGlobalSnack('Carregado com sucesso.','success');
      }).catch((e)=>{
          console.log(e);
          setLoadingConsult(false);
          openGlobalSnack('Atualize a pagina','error');
      })
    },[loadingConsult]);

    useEffect(()=>{
       const nshow=propostal.filter(pro=>{
          if(statusFilters.length>0&&!statusFilters.includes(pro.status))return false;
          if(usersIdFilters.length>0&&!usersIdFilters.includes((pro.user_id||0).toString()))return false;
          if(bankFilters.length>0&&!bankFilters.includes(pro.bank||''))return false;
          return true;
       });
       setShowedPropostals(nshow);
    },[propostal,statusFilters,usersIdFilters,bankFilters]);

    return (<Box sx={{maxWidth:1100,mx:'auto',px:2,py:1}}>
        <Typography variant="h5" sx={{fontWeight:600,mb:1}}>Dashboard CLT</Typography>

        <Paper variant="outlined" sx={{p:2,mb:2}}>
            <DatepickerRange disabled={loadingConsult} title="Período de consulta." onUpdated={(s,e)=>setDate({start:s,end:e})}/>
            <Divider sx={{my:1.5}}/>
            <Stack direction="row" flexWrap="wrap" justifyContent="center" alignItems="center" gap={1}>
                <MultipleSelectChip label='Status' items={Object.values(CltPropostalStatus)} onChange={setStatusFilters}/>
                <MultipleSelectChip label='Banco' items={bankItems} onChange={setBankFilters}/>
                <MultipleSelectChip label='Usuário' items={userData.map(u=>({label:u.email,value:u.id}))} onChange={setSelectedUserId}/>
            </Stack>
            <Box sx={{textAlign:'center',mt:1.5}}>
                <LoadingButton loading={loadingConsult} onClick={()=>setLoadingConsult(true)} variant="contained">Consultar</LoadingButton>
            </Box>
        </Paper>

        {propostal.length<1?
           <Typography sx={{textAlign:'center',color:'text.secondary',mt:4}}>
             {loadingConsult?'Carregando...':'Selecione um período e clique em Consultar.'}
           </Typography>
        :<>
            <BasicInformation propostals={showedPropostsals} rows={rows}/>

            <GraphSection title="Produção por banco">
                <CltGraphTotalByGenericParam param="bank" propostals={showedPropostsals} showComission={true}
                    label="" showParamName={cltBankLabel}/>
            </GraphSection>
            <GraphSection title="Produção por usuário">
                <CltGraphTotalByGenericParam param="user_id" propostals={showedPropostsals} label=""
                    showParamName={(s)=>{
                        for(let u of userData){ if(u.id.toString()==s)return `${u.id} ${u.email}`; }
                        return 'Sem usuário';
                    }}/>
            </GraphSection>
            <GraphSection title="Produção por canal">
                <CltGraphTotalByGenericParam param="chatSource" propostals={showedPropostsals} showComission={true}
                    label="" showParamName={chatSourceToPTBR}/>
            </GraphSection>
            <GraphSection title="Produção por fonte">
                <CltGraphTotalByGenericParam param="source" propostals={showedPropostsals} showComission={true} label=""/>
            </GraphSection>
            <GraphSection title="Produção por status">
                <CltGraphTotalByGenericParam param="status" propostals={showedPropostsals} showComission={false} label=""/>
            </GraphSection>
        </>}
    </Box>)
}

/** Seção de gráfico com título e rolagem horizontal (os charts têm largura fixa). */
function GraphSection({title,children}:{title:string,children:React.ReactNode}){
    return (
        <Paper variant="outlined" sx={{p:2,mb:2}}>
            <Typography variant="subtitle1" sx={{fontWeight:600,textAlign:'center',mb:1}}>{title}</Typography>
            <Box sx={{overflowX:'auto'}}>{children}</Box>
        </Paper>
    );
}

function BasicInformation({propostals,rows}:{propostals:Array<CltPropostalInfo>,rows:Array<CltSupportRowInfo>}){
    const [basicInfo,setBasicInfo]=useState(getZeroBasicInfo())
    useEffect(()=>{
       let nBasicInfo=getZeroBasicInfo();
       for(let prop of propostals){
          nBasicInfo.totalProd+=prop.liquidValue;
          nBasicInfo.totalComission+=prop.totalComission;
          nBasicInfo.totalPartnerComission+=prop.partnerComission;
       }
       nBasicInfo.numberOfPropostals=propostals.length;
       nBasicInfo.liquidComission=nBasicInfo.totalComission-nBasicInfo.totalPartnerComission;
       nBasicInfo.totalProd/=100;
       nBasicInfo.liquidComission/=100;
       nBasicInfo.totalPartnerComission/=100;
       nBasicInfo.totalComission/=100;
       setBasicInfo(nBasicInfo);
    },[propostals,rows]);

    // Quebra de produção por banco (tooltip do card de Produção Total).
    const byBank=useMemo(()=>{
        const acc:{[k:string]:{prod:number,count:number}}={};
        for(const p of propostals){
            const key=p.bank||'desconhecido';
            if(!acc[key])acc[key]={prod:0,count:0};
            acc[key].prod+=p.liquidValue/100;
            acc[key].count++;
        }
        const entries=Object.entries(acc).sort((a,b)=>b[1].prod-a[1].prod);
        if(entries.length===0)return null;
        return <div>{entries.map(([k,v])=>
            <p key={k}>{`${cltBankLabel(k)}: ${normalizeBrCurrency(v.prod)} (${v.count})`}</p>
        )}</div>;
    },[propostals]);

    return(<Box sx={{display:'flex',flexWrap:'wrap',justifyContent:'center',gap:1.5,mt:1,mb:1}}>
       <DisplayCard primary="Produção Total" toolTip={byBank} secondary={normalizeBrCurrency(basicInfo.totalProd)}/>
       <DisplayCard primary="Número de Propostas" secondary={basicInfo.numberOfPropostals.toString()}/>
       <DisplayCard primary="Comissão Total" secondary={normalizeBrCurrency(basicInfo.totalComission)}/>
       <DisplayCard primary="Comissão Parceiro" secondary={normalizeBrCurrency(basicInfo.totalPartnerComission)}/>
       <DisplayCard primary="Comissão Liquida" secondary={normalizeBrCurrency(basicInfo.liquidComission)}/>
    </Box>);

   function getZeroBasicInfo(){
      return {
         totalProd:0,
         numberOfPropostals:0,
         totalComission:0,
         totalPartnerComission:0,
         liquidComission:0,
    }
   }
}
